import type React from "react";
import {Box, Typography} from "@mui/material";
import {COLORS} from "~/style/colors-ts";
import {MONO_FONT} from "~/style/fonts";

export type KpiTone = 'neutral' | 'good' | 'warn' | 'bad';

export type KpiCell = {label: string, value: React.ReactNode, tone?: KpiTone, hint?: string};

const TONE_COLOR: Record<KpiTone, string> = {
    neutral: COLORS.grey[900],
    good: COLORS.success.main,
    warn: COLORS.warning.main,
    bad: COLORS.error.main,
};

/**
 * A row of equal KPI cells (label over a large value). Two per row on phones; the 1px gap shows the
 * background as the dividers, and an odd last cell takes the whole row.
 */
export const KpiStrip: React.FC<{cells: KpiCell[]}> = ({cells}) => (
    <Box sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr 1fr', md: `repeat(${cells.length}, minmax(0, 1fr))`},
        gap: '1px',
        bgcolor: COLORS.grey[150],
        border: 1,
        borderColor: COLORS.grey[150],
        borderRadius: 1.5,
        overflow: 'hidden',
        '& > :last-child:nth-of-type(odd)': {gridColumn: {xs: '1 / -1', md: 'auto'}},
    }}>
        {cells.map(cell => (
            <Box
                key={cell.label}
                title={cell.hint}
                sx={{minWidth: 0, p: 1.5, bgcolor: COLORS.white[900]}}
            >
                <Typography variant="caption" color="grey.700" noWrap component="div">{cell.label}</Typography>
                <Typography sx={{fontFamily: MONO_FONT, fontSize: 20, fontWeight: 600, color: TONE_COLOR[cell.tone ?? 'neutral']}} noWrap>
                    {cell.value}
                </Typography>
            </Box>
        ))}
    </Box>
);
